/**
 * MigrateMyLabsCard — iter754 (Feb 2026)
 *
 * 1-click patient-facing lab unification trigger. Calls
 * POST /api/labs/migrate-my-labs and shows live progress + per-source
 * breakdown. Idempotent — safe to click repeatedly.
 *
 * Loads current status on mount via GET /api/labs/migration-status.
 */
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import {
  Database, Loader2, CheckCircle2, RefreshCw, TrendingUp, Layers,
} from "lucide-react";

const SOURCE_LABELS_BG = {
  lab_analyses:    "Лабораторни анализи (Bulk PDF)",
  specialist_labs: "Специализирани лаборатории (DUTCH)",
  thyroid_labs:    "Тиреоидни панели",
  wh_lab_uploads:  "Women's Health lab uploads",
  lab_snapshots:   "Стари lab snapshots",
  raw_dna_23andme: "23andMe DNA",
  raw_dna_ancestry: "Ancestry DNA",
};

export default function MigrateMyLabsCard() {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const [status, setStatus] = useState(null);
  const [migrating, setMigrating] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/labs/migration-status`);
      setStatus(data);
    } catch {
      // silent — endpoint is optional context
    }
  }, [API]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const handleMigrate = async () => {
    setMigrating(true);
    setError(null);
    setResult(null);
    try {
      const { data } = await axios.post(`${API}/labs/migrate-my-labs`);
      setResult(data);
      await loadStatus();
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Migration failed");
    } finally {
      setMigrating(false);
    }
  };

  const cardCls = isDark
    ? "bg-slate-900/60 border-slate-800"
    : "bg-white border-slate-200";

  return (
    <section
      data-testid="migrate-my-labs-card"
      className={`p-5 rounded-2xl border ${cardCls}`}
    >
      <div className="flex items-start gap-3 mb-4">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
          isDark ? "bg-indigo-900/60" : "bg-indigo-100"
        }`}>
          <Database className="w-5 h-5 text-indigo-500" />
        </div>
        <div className="flex-1 min-w-0">
          <div className={`text-[10px] uppercase font-bold tracking-widest mb-0.5 ${
            isDark ? "text-indigo-300" : "text-indigo-600"
          }`}>
            Обединяване на лабораторни данни
          </div>
          <h2 className={`text-base font-semibold ${isDark ? "text-slate-100" : "text-slate-900"}`}>
            Обедини моите лабораторни данни
          </h2>
          <p className={`text-[12px] leading-relaxed mt-1 ${isDark ? "text-slate-400" : "text-slate-600"}`}>
            Обединява данни от всичките ви лабораторни колекции (DUTCH, тиреоидни, метаболитни, хормонални, DNA)
            в единен каноничен запис, който всичките ви Digital Twins четат.
            <b> Идемпотентно</b> — безопасно за многократно стартиране.
          </p>
        </div>
      </div>

      {/* Current status */}
      {status && (
        <div className={`grid grid-cols-2 gap-3 mb-4`}>
          <div className={`p-3 rounded-lg ${isDark ? "bg-slate-950/60" : "bg-slate-50"}`}>
            <div className={`text-[10px] uppercase font-bold ${isDark ? "text-slate-400" : "text-slate-500"}`}>
              Общо записи
            </div>
            <div className={`text-2xl font-bold ${isDark ? "text-slate-100" : "text-slate-900"}`}
                 data-testid="migrate-total-entries">
              {status.total_entries ?? 0}
            </div>
          </div>
          <div className={`p-3 rounded-lg ${isDark ? "bg-slate-950/60" : "bg-slate-50"}`}>
            <div className={`text-[10px] uppercase font-bold ${isDark ? "text-slate-400" : "text-slate-500"}`}>
              Различни маркери
            </div>
            <div className={`text-2xl font-bold ${isDark ? "text-slate-100" : "text-slate-900"}`}
                 data-testid="migrate-distinct-markers">
              {status.distinct_markers ?? 0}
            </div>
          </div>
        </div>
      )}

      {/* Per-source breakdown */}
      {status?.sources && Object.keys(status.sources).length > 0 && (
        <div className="mb-4">
          <div className={`text-[10px] uppercase font-bold mb-2 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
            По източник (текущо в lab_entries)
          </div>
          <div className="space-y-1.5">
            {Object.entries(status.sources).map(([src, n]) => (
              <div key={src} className="flex items-center justify-between text-[12px]">
                <span className={`flex items-center gap-1.5 ${isDark ? "text-slate-300" : "text-slate-700"}`}>
                  <Layers className="w-3 h-3 opacity-60" />
                  {SOURCE_LABELS_BG[src] || src}
                </span>
                <span className={`font-semibold tabular-nums ${isDark ? "text-emerald-300" : "text-emerald-700"}`}>
                  {n}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Trigger button */}
      <button
        type="button"
        onClick={handleMigrate}
        disabled={migrating}
        data-testid="migrate-my-labs-btn"
        className={`w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all ${
          migrating
            ? "bg-slate-500 text-white cursor-not-allowed opacity-70"
            : isDark
              ? "bg-indigo-600 hover:bg-indigo-500 text-white shadow-lg shadow-indigo-900/40"
              : "bg-indigo-600 hover:bg-indigo-700 text-white shadow-md"
        }`}
      >
        {migrating ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Обединяване в ход...
          </>
        ) : (
          <>
            <RefreshCw className="w-4 h-4" />
            Стартирай обединяване
          </>
        )}
      </button>

      {/* Result banner */}
      {result && (
        <div
          data-testid="migrate-my-labs-result"
          className={`mt-4 p-4 rounded-xl border ${
            isDark
              ? "bg-emerald-500/10 border-emerald-500/40 text-emerald-100"
              : "bg-emerald-50 border-emerald-200 text-emerald-900"
          }`}
        >
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle2 className="w-5 h-5 text-emerald-500" />
            <span className="font-semibold">Готово</span>
          </div>
          <div className="text-[12px] space-y-1">
            <div>
              <b>Общо записи преди:</b> {result.canonical_entries_before} → <b>след:</b>{" "}
              {result.canonical_entries_after}
              {result.delta > 0 && (
                <span className="ml-2 inline-flex items-center gap-1 text-emerald-500 font-bold">
                  <TrendingUp className="w-3 h-3" /> +{result.delta}
                </span>
              )}
            </div>
            <div>
              <b>Insert:</b> {result.grand_inserted} · <b>Update:</b> {result.grand_updated}
            </div>
            <div>
              <b>Различни маркери:</b> {result.distinct_markers}
            </div>
          </div>

          {result.stats_per_source && (
            <div className={`mt-3 pt-3 border-t ${isDark ? "border-emerald-800/40" : "border-emerald-200"}`}>
              <div className="text-[10px] uppercase font-bold mb-1.5 opacity-70">
                Обработени по източник
              </div>
              <div className="space-y-1">
                {Object.entries(result.stats_per_source).map(([src, s]) => (
                  <div key={src} className="text-[11px] flex justify-between font-mono">
                    <span>{SOURCE_LABELS_BG[src] || src}</span>
                    <span className="opacity-80">
                      scanned={s.scanned} · ins={s.inserted} · upd={s.updated}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {error && (
        <div
          data-testid="migrate-my-labs-error"
          className={`mt-4 p-3 rounded-lg text-[12px] ${
            isDark ? "bg-red-500/10 text-red-300" : "bg-red-50 text-red-800"
          }`}
        >
          {String(error)}
        </div>
      )}
    </section>
  );
}
